import { locationsApi } from 'api'
import { Confirmation } from 'ui/molecules/Confirmation'
import { useEffect, useState } from 'react'
import { useDispatch } from 'react-redux'
import { URLS } from 'constants/urls'
import { useNavigate, useParams } from 'react-router-dom'
import { useAppSelector } from 'redux/hooks'
import {
  LOCATION_DETAILS_TITLE,
  LOCATION_EDIT_CONFIRMATION,
  LOCATION_EDIT_TITLE,
  SUCCESS_ACTIVATE,
  SUCCESS_INACTIVATE,
} from 'texts/locationEdit'
import { SUCCESSFUL_CHANGE_MODAL } from 'texts/profileConfirmModal'
import { Container } from 'ui/molecules/Container'
import { FormValues } from '../LocationDetailsForm/types'
import { LocationDetailsForm } from '../LocationDetailsForm'
import { HirePointData } from 'api/locations/types'
import {
  isMigratedUserSelector,
  isOperatorSelector,
  userDataSelector,
} from 'redux/login/selectors'
import { getDetailsBreadcrumbsList } from 'utils/getDetailsBreadcrumbsList'
import { Typography } from 'ui/atoms/Typography'
import { DELETE_LOCATION_MODAL } from 'texts/locationList'
import {
  ACCOUNT_STATUSES,
  READONLY_STATUSES,
  REGISTRATION_STATUSES,
} from 'api/auth/constants'
import { setNotificationMessage } from 'redux/notifications/slice'
import { useApiRequest } from 'hooks/useApiRequest'
import { OperatorActionButtons } from 'ui/components/OperatorActionButtons'
import { useCompanyId } from 'hooks/useCompanyId'
import { ErrorResponse } from 'api/types'
import { NEW_LOCATION_ERROR_CODES } from '../LocationDetailsForm/constants'
import { setNewLocationError } from 'redux/locations/slice'
import { FormActionButtons } from 'ui/components/FormActionButtons'
import { LOCATION_DETAILS_FORM_ID } from 'modules/Locations/constants'
import { LocationsSubmitButton } from '../LocationsSubmitButton'
import { useMigrationRedirect } from 'hooks/useMigrationRedirect'
import styles from './styles.module.scss'

export const LocationEdit = () => {
  const params = useParams()
  const { accountStatus, sectionsStatuses } = useAppSelector(userDataSelector)
  const [initialData, setInitialData] = useState<Partial<HirePointData>>({})
  const [isConfirmationModalOpen, setIsConfirmationModalOpen] = useState(false)
  const isOperator = useAppSelector(isOperatorSelector)
  const isMigratedUser = useAppSelector(isMigratedUserSelector)
  const isMigratedUserFlow = isMigratedUser && !isOperator
  const dispatch = useDispatch()
  const isAccountApproved =
    isOperator || accountStatus === ACCOUNT_STATUSES.APPROVED

  const isIncomplete =
    [
      REGISTRATION_STATUSES.INCOMPLETE,
      REGISTRATION_STATUSES.REQUESTED,
    ].includes(sectionsStatuses?.locationsStatus || '') && !isOperator

  const navigate = useNavigate()
  const companyId = useCompanyId()

  const { redirect } = useMigrationRedirect()

  const handleCancelClick = () => {
    if (isAccountApproved) {
      const path = isOperator
        ? `${URLS.LOCATIONS}${URLS.LIST_OF_LOCATIONS}/${companyId}`
        : `${URLS.LOCATIONS}${URLS.LIST_OF_LOCATIONS}`
      navigate(path)
    } else {
      navigate(`${URLS.LOCATIONS}${URLS.LIST_OF_LOCATIONS}/add`)
    }
  }

  useEffect(() => {
    if (
      !isAccountApproved &&
      READONLY_STATUSES.includes(sectionsStatuses?.locationsStatus || '')
    ) {
      navigate(`${URLS.LOCATIONS}${URLS.LIST_OF_LOCATIONS}`)
    }
  })

  const { apiRequest: updateHirePoint, loading: updateHirePointLoading } =
    useApiRequest(
      (hirePointId, values) =>
        locationsApi.updateHirePoint(Number(hirePointId), values),
      undefined,
      true,
      {
        withoutNotification: false,
        isThrowError: false,
        onError: (error) => {
          const errorResponse = error as ErrorResponse
          if (
            NEW_LOCATION_ERROR_CODES.includes(
              errorResponse.responseStatus.errorCode
            )
          ) {
            dispatch(
              setNewLocationError(errorResponse.responseStatus.errorCode)
            )
          }
        },
      },
      false
    )
  const { apiRequest: changeHirePointStatus } = useApiRequest(
    (hirePointId, status) =>
      locationsApi.changeHirePointState(Number(hirePointId), status)
  )
  const { apiRequest: fetchHirePointInfo, loading: fetchHirePointInfoLoading } =
    useApiRequest((hirePointId) => locationsApi.getHirePointInfo(hirePointId))

  const hirePointId = params.id

  const breadcrumbList = getDetailsBreadcrumbsList(
    isOperator,
    String(companyId),
    'locations'
  )

  const handleModalClose = () => {
    setIsConfirmationModalOpen(false)
  }

  const getHirePointInfo = async () => {
    const response = await fetchHirePointInfo(Number(hirePointId))
    if (response) {
      setInitialData({
        ...response.data.hirePointInfo,
        locationId:
          response.data.hirePointInfo.locationId ||
          response.data.hirePointInfo.pendingLocationId,
        cityId:
          response.data.hirePointInfo.cityId ||
          response.data.hirePointInfo.pendingCityId,
      })
    }
  }

  const handleConfirm = async () => {
    const response = await changeHirePointStatus(
      hirePointId,
      !initialData.isActive
    )
    if (response) {
      dispatch(
        setNotificationMessage({
          notificationMessage: initialData.isActive
            ? SUCCESS_INACTIVATE
            : SUCCESS_ACTIVATE,
        })
      )
      getHirePointInfo()
      setIsConfirmationModalOpen(false)
    }
  }

  useEffect(() => {
    if (hirePointId) {
      getHirePointInfo()
    }
  }, [])

  const handleSubmit = async (data: Partial<FormValues>) => {
    const values = {
      ...data,
      locationId: data.locationId || null,
      cityId: data.cityId || null,
      waitingHours: {
        waitingHours: data.waitingHours,
        waitingHourPrice: data.waitingHourPrice,
        waitingHourRange: data.waitingHourRange,
        waitingHourFeeType: data.waitingHourFeeType,
      },
      companyID: Number(companyId),
    }
    delete values.phoneCor
    delete values.pendingLocationId
    delete values.pendingCityId

    const response = await updateHirePoint(Number(hirePointId), values)
    if (response) {
      dispatch(
        setNotificationMessage({
          notificationMessage: SUCCESSFUL_CHANGE_MODAL.TITLE,
        })
      )
      if (isMigratedUserFlow) {
        redirect(companyId, 'locationsStatus')
      }
      if (isAccountApproved) {
        const path = isOperator
          ? `${URLS.LOCATIONS}${URLS.LIST_OF_LOCATIONS}/${companyId}`
          : `${URLS.LOCATIONS}${URLS.LIST_OF_LOCATIONS}`
        navigate(path)
      } else {
        navigate(`${URLS.LOCATIONS}${URLS.LIST_OF_LOCATIONS}/add`)
      }
    }
  }

  const header = (
    <>
      {isConfirmationModalOpen && (
        <Confirmation
          confirmModalProps={{
            question: `${
              LOCATION_EDIT_CONFIRMATION.CONFIRMATION_MODAL.QUESTION
            } ${initialData?.locationName}${
              initialData?.isActive
                ? LOCATION_EDIT_CONFIRMATION.CONFIRMATION_MODAL.INACTIVE
                : LOCATION_EDIT_CONFIRMATION.CONFIRMATION_MODAL.ACTIVATE
            }`,
            onConfirm: handleConfirm,
            onClose: handleModalClose,
            title: initialData.isActive
              ? `${DELETE_LOCATION_MODAL.DEACTIVATE} ${initialData.locationName}`
              : `${DELETE_LOCATION_MODAL.ACTIVATE} ${initialData.locationName}`,
          }}
        />
      )}
      <Typography name="H5">{LOCATION_EDIT_TITLE}</Typography>
    </>
  )
  return (
    <Container
      title={header}
      breadcrumbList={breadcrumbList}
      currentPageLabel={LOCATION_DETAILS_TITLE}
      withNavigation={isOperator}
      actionButtons={(id: string) => (
        <OperatorActionButtons companyId={id} section="Locations" />
      )}
      rightBlock={() => (
        <div className={styles['button-container']}>
          <FormActionButtons
            handleBack={handleCancelClick}
            loading={updateHirePointLoading}
            formId={LOCATION_DETAILS_FORM_ID}
          />
          {isIncomplete && <LocationsSubmitButton />}
        </div>
      )}
      loading={fetchHirePointInfoLoading}
    >
      <LocationDetailsForm
        formValues={initialData}
        handleSubmit={handleSubmit}
        isLoading={updateHirePointLoading}
        isEditing
        formId={LOCATION_DETAILS_FORM_ID}
      />
    </Container>
  )
}
